import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE, {
  deletePost as deletePostRequest,
  getPostDeleteErrorMessage,
  withApiCredentials,
} from "../api";
import { trackProductEvent } from "../analytics/trackEvent";
import { didPostInfoContextChange } from "../components/profile/edit-post/editPostPayloadBuilder";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import i18n from "../i18n";
import {
  mergePostsById,
  sortPostsNewestFirst,
} from "../hooks/usePaginatedUserPosts";
import {
  getCatchSubmitRefreshEventName,
  reportBackgroundRefreshBlocked,
  setCatchModalActive,
  setCatchSubmitActive,
  shouldBlockBackgroundRefresh,
} from "./catchSubmitGate";

const logCatchTx = (event, details = {}) => {
  try {
    console.warn("[CATCH_TX]", event, {
      attemptId:
        Number.isFinite(details?.attemptId) ? details.attemptId : details?.attemptId ?? null,
      ...details,
    });
  } catch {
    // no-op
  }
};

function normalizeComments(comments = []) {
  return comments.map((comment) => ({
    ...comment,
    likes: Array.isArray(comment.likes) ? comment.likes : [],
    replies: Array.isArray(comment.replies)
      ? comment.replies.map((reply) => ({
          ...reply,
          likes: Array.isArray(reply.likes) ? reply.likes : [],
        }))
      : [],
  }));
}

const FEED_PAGE_LIMIT = 20;
const AUTHORITATIVE_RESTORE_PAGE_LIMIT = 100;
const FEED_POLL_INTERVAL_MS = 60000;
const FEED_MODE_HEADER = "X-Feed-Mode";
const FEED_SNAPSHOT_HEADER = "X-Feed-Snapshot";
const PERSONALIZED_FEED_MODE = "personalized";
const RECENT_LOCAL_POST_UPDATE_TTL_MS = 20000;
const PAST_TRIP_CATCH_MAP_RETURN_KEY =
  "reelitpro:past-trip-catch-map-return";

export function getFeedSessionKey(userId, authToken) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return null;

  const normalizedAuthToken = String(authToken || "").trim();
  return normalizedAuthToken
    ? `${normalizedUserId}:${normalizedAuthToken}`
    : `${normalizedUserId}:cookie`;
}

function normalizeFeedPosts(feedPosts) {
  return feedPosts.map((post) => {
    if (post.catch && post.catch.catch) {
      return {
        ...post,
        likes: Array.isArray(post.likes) ? post.likes : [],
        comments: normalizeComments(post.comments),
        catch: {
          ...post.catch.catch,
          rating: post.catch.catch.rating || 0,
        },
      };
    }

    if (post.catch) {
      return {
        ...post,
        comments: normalizeComments(post.comments),
        catch: {
          ...post.catch,
          rating: post.catch.rating || 0,
        },
      };
    }

    if (post.postType === "opinion") {
      return {
        ...post,
        likes: Array.isArray(post.likes) ? post.likes : [],
        comments: normalizeComments(post.comments),
      };
    }

    return {
      ...post,
      comments: normalizeComments(post.comments),
      catch: {
        fish: post.fish || i18n.t("feed_unknown_fish", { defaultValue: "Unknown" }),
        date: post.date || post.postDate || "-",
        time: post.time || "-",
        coordinates: post.coordinates || "-",
        rating: post.rating || 0,
      },
    };
  });
}

export function applyPostLikeMutation(post, { liked, likesCount, actorUserId }) {
  const currentLikes = Array.isArray(post.likes) ? [...post.likes] : [];
  const normalizedActorUserId = String(actorUserId || "").trim();
  const requestedLikesCount = Math.max(0, Number(likesCount) || 0);
  const normalizedLikesCount =
    liked && normalizedActorUserId
      ? Math.max(1, requestedLikesCount)
      : requestedLikesCount;
  const otherLikes = normalizedActorUserId
    ? currentLikes.filter(
        (likeUserId) => String(likeUserId) !== normalizedActorUserId
      )
    : currentLikes;
  const otherLikesTarget =
    liked && normalizedActorUserId
      ? Math.max(0, normalizedLikesCount - 1)
      : normalizedLikesCount;
  let nextLikes = otherLikes.slice(0, otherLikesTarget);
  const usedLikeIds = new Set(nextLikes.map((likeUserId) => String(likeUserId)));
  let placeholderIndex = 0;

  while (nextLikes.length < otherLikesTarget) {
    const placeholder = `like-placeholder-${placeholderIndex}`;
    placeholderIndex += 1;
    if (placeholder === normalizedActorUserId || usedLikeIds.has(placeholder)) {
      continue;
    }
    nextLikes.push(placeholder);
    usedLikeIds.add(placeholder);
  }

  if (liked && normalizedActorUserId) {
    nextLikes.push(normalizedActorUserId);
  }

  return {
    ...post,
    likes: nextLikes,
  };
}

export function countPublicFeedRows(feedPosts) {
  return feedPosts.filter(
    (post) => post?.feedContext?.source !== "curated"
  ).length;
}

function getPublicFeedPostIds(feedPosts) {
  return feedPosts
    .filter((post) => post?.feedContext?.source !== "curated")
    .map((post) => String(post?.id || "").trim())
    .filter(Boolean);
}

function getTemporaryPinSignature(feedPosts) {
  const pinnedPost = feedPosts.find((post) => post?.is_pinned === true);
  return String(pinnedPost?.id || "").trim();
}

function sortGlobalFeedPosts(posts) {
  return sortPostsNewestFirst(posts).sort(
    (left, right) =>
      Number(right?.is_pinned === true) - Number(left?.is_pinned === true)
  );
}

function mergeGlobalFeedPostsById(existingPosts, incomingPosts, options) {
  return sortGlobalFeedPosts(
    mergePostsById(existingPosts, incomingPosts, options)
  );
}

export function mergeRankedFeedPostsById(
  existingPosts,
  incomingPosts,
  { incomingWins = true } = {}
) {
  const result = [];
  const positionsById = new Map();
  const addPost = (post, replaceExisting) => {
    const postId = String(post?.id || "").trim();
    if (!postId) return;
    const existingPosition = positionsById.get(postId);
    if (existingPosition == null) {
      positionsById.set(postId, result.length);
      result.push(post);
    } else if (replaceExisting) {
      result[existingPosition] = post;
    }
  };

  existingPosts.forEach((post) => addPost(post, false));
  incomingPosts.forEach((post) => addPost(post, incomingWins));
  return result;
}

function mergeFeedPostsByMode(mode, existingPosts, incomingPosts, options) {
  return mode === PERSONALIZED_FEED_MODE
    ? mergeRankedFeedPostsById(existingPosts, incomingPosts, options)
    : mergeGlobalFeedPostsById(existingPosts, incomingPosts, options);
}

function getFeedResponseMetadata(response) {
  const mode = String(response?.headers?.get?.(FEED_MODE_HEADER) || "")
    .trim()
    .toLowerCase();
  const snapshotToken = String(
    response?.headers?.get?.(FEED_SNAPSHOT_HEADER) || ""
  ).trim();
  return {
    mode: mode === PERSONALIZED_FEED_MODE && snapshotToken
      ? PERSONALIZED_FEED_MODE
      : "chronological",
    snapshotToken,
  };
}

function clearTemporaryPinMetadata(posts) {
  return posts.map((post) => {
    if (post?.is_pinned !== true && !post?.pinned_until) return post;
    const nextPost = { ...post };
    delete nextPost.is_pinned;
    delete nextPost.pinned_until;
    return nextPost;
  });
}

export async function loadAuthoritativePostRestore(
  postId,
  { authToken = "", fetchImpl = fetch } = {}
) {
  const normalizedPostId = String(postId || "").trim();
  if (!normalizedPostId || typeof fetchImpl !== "function") {
    return { status: "failed" };
  }

  const seenRawPostIds = new Set();
  let offset = 0;
  let feedSnapshotToken = "";

  try {
    while (true) {
      const query = new URLSearchParams({
        limit: String(AUTHORITATIVE_RESTORE_PAGE_LIMIT),
        offset: String(offset),
      });
      if (feedSnapshotToken) {
        query.set("feed_snapshot", feedSnapshotToken);
      }
      const response = await fetchImpl(
        `${API_BASE}/posts?${query.toString()}`,
        withApiCredentials(
          authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined
        )
      );

      if (!response?.ok) {
        return { status: "failed" };
      }

      const responseMetadata = getFeedResponseMetadata(response);
      if (responseMetadata.mode === PERSONALIZED_FEED_MODE) {
        if (
          feedSnapshotToken &&
          responseMetadata.snapshotToken !== feedSnapshotToken
        ) {
          return { status: "failed" };
        }
        feedSnapshotToken = responseMetadata.snapshotToken;
      }

      const page = await response.json();
      if (!Array.isArray(page)) {
        return { status: "failed" };
      }

      const exactPost = page.find(
        (post) => String(post?.id || "").trim() === normalizedPostId
      );
      if (exactPost) {
        const [post] = normalizeFeedPosts([exactPost]);
        return post ? { status: "found", post } : { status: "failed" };
      }

      const rawPostIds = getPublicFeedPostIds(page);
      if (countPublicFeedRows(page) < AUTHORITATIVE_RESTORE_PAGE_LIMIT) {
        return { status: "missing" };
      }

      const unseenRawPostIds = rawPostIds.filter(
        (rawPostId) => !seenRawPostIds.has(rawPostId)
      );
      if (unseenRawPostIds.length === 0) {
        return { status: "failed" };
      }

      rawPostIds.forEach((rawPostId) => seenRawPostIds.add(rawPostId));
      offset += rawPostIds.length;
    }
  } catch {
    return { status: "failed" };
  }
}

export function applyRecentPostLikeUpdates(
  feedPosts,
  recentLikeUpdates,
  requestStartedAt
) {
  if (!recentLikeUpdates.size) return feedPosts;

  return feedPosts.map((post) => {
    const recent = recentLikeUpdates.get(post.id);
    if (!recent) return post;

    const mustPreserveLocalState =
      recent.mutationPhase === "optimistic" ||
      recent.mutationUpdatedAt > requestStartedAt;

    if (!mustPreserveLocalState) return post;

    return applyPostLikeMutation(post, recent);
  });
}

export function cleanupAcceptedPostLikeUpdates(
  recentLikeUpdates,
  requestStartedAt
) {
  recentLikeUpdates.forEach((recent, postId) => {
    const isPending = recent.mutationPhase === "optimistic";
    const responseStartedBeforeMutation =
      recent.mutationUpdatedAt > requestStartedAt;

    if (!isPending && !responseStartedBeforeMutation) {
      recentLikeUpdates.delete(postId);
    }
  });
}

const mergeUpdatedPost = (currentPost, updatedPost) => ({
  ...currentPost,
  ...updatedPost,
  catch: updatedPost?.catch
    ? {
        ...(currentPost.catch || {}),
        ...updatedPost.catch,
      }
    : currentPost.catch,
});

function valuesMatch(left, right) {
  return String(left ?? "") === String(right ?? "");
}

function fetchedPostIncludesRecentUpdate(fetchedPost, recentPost) {
  if (!fetchedPost || !recentPost) return false;

  if (
    Object.prototype.hasOwnProperty.call(recentPost, "caption") &&
    !valuesMatch(fetchedPost.caption, recentPost.caption)
  ) {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(recentPost, "mediaUrl") &&
    !valuesMatch(fetchedPost.mediaUrl, recentPost.mediaUrl)
  ) {
    return false;
  }

  if (
    Object.prototype.hasOwnProperty.call(recentPost, "media_url") &&
    !valuesMatch(fetchedPost.media_url, recentPost.media_url)
  ) {
    return false;
  }

  const recentCatch =
    recentPost.catch && typeof recentPost.catch === "object"
      ? recentPost.catch
      : null;
  if (!recentCatch) return true;

  const fetchedCatch =
    fetchedPost.catch && typeof fetchedPost.catch === "object"
      ? fetchedPost.catch
      : {};

  return Object.keys(recentCatch).every((fieldName) =>
    valuesMatch(fetchedCatch[fieldName], recentCatch[fieldName])
  );
}

function buildRecentPostOverlay(fetchedPost, recentPost) {
  const overlay = {
    id: recentPost.id,
  };

  if (Object.prototype.hasOwnProperty.call(recentPost, "caption")) {
    overlay.caption = recentPost.caption;
  }
  if (Object.prototype.hasOwnProperty.call(recentPost, "mediaUrl")) {
    overlay.mediaUrl = recentPost.mediaUrl;
  }
  if (Object.prototype.hasOwnProperty.call(recentPost, "media_url")) {
    overlay.media_url = recentPost.media_url;
  }
  if (Object.prototype.hasOwnProperty.call(recentPost, "resolvedMediaUrl")) {
    overlay.resolvedMediaUrl = recentPost.resolvedMediaUrl;
  }
  if (Object.prototype.hasOwnProperty.call(recentPost, "videoMetadata")) {
    overlay.videoMetadata = recentPost.videoMetadata;
  }
  if (Object.prototype.hasOwnProperty.call(recentPost, "_mediaRefreshToken")) {
    overlay._mediaRefreshToken = recentPost._mediaRefreshToken;
  }

  if (recentPost.catch && typeof recentPost.catch === "object") {
    overlay.catch = recentPost.catch;
  }

  return mergeUpdatedPost(fetchedPost, overlay);
}

function applyRecentLocalPostUpdates(feedPosts, recentUpdates) {
  if (!recentUpdates.size) return feedPosts;

  const now = Date.now();

  return feedPosts.map((post) => {
    const recent = recentUpdates.get(post.id);
    if (!recent) return post;

    if (recent.expiresAt <= now) {
      recentUpdates.delete(post.id);
      return post;
    }

    if (fetchedPostIncludesRecentUpdate(post, recent.post)) {
      recentUpdates.delete(post.id);
      return post;
    }

    return buildRecentPostOverlay(post, recent.post);
  });
}

export default function usePostsAndComposerState({
  user,
  setUser,
  setActiveTab,
  profileUi,
  onSuppressPickerOpenNavigation,
  onSuppressPickerReturnNavigation,
  onForcePickerReturnNavigation,
  authTransitioning = false,
  isFeedRoute = true,
  authoritativeRestoreKey = "",
  authoritativeRestorePostId = "",
}) {
  const {
    setShowProfile,
    setShowUserProfile,
    setSelectedUser,
    setProfileFocusPostRequest,
    setUserProfileFocusPostRequest,
  } = profileUi;

  const [pickedCoordinates, setPickedCoordinates] = useState("");
  const [draftCatchMedia, setDraftCatchMedia] = useState(null);
  const [draftCatchFormState, setDraftCatchFormState] = useState(null);
  const [draftExifDate, setDraftExifDate] = useState(null);
  const [draftExifTime, setDraftExifTime] = useState(null);
  const [editPostMapSelection, setEditPostMapSelection] = useState(null);
  const [showInfo, _setShowInfo] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [isCreatePostSubmitting, setIsCreatePostSubmitting] = useState(false);
  const [isPickingLocation, setIsPickingLocation] = useState(false);
  const [pickerSource, setPickerSource] = useState(null);
  const [posts, setPosts] = useState([]);
  const [initialFeedResolved, setInitialFeedResolved] = useState(false);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [isLoadingMorePosts, setIsLoadingMorePosts] = useState(false);
  const [authoritativeRestoreState, setAuthoritativeRestoreState] = useState({
    requestKey: "",
    result: null,
  });
  const [sharePost, setSharePost] = useState(null);
  const [levelUpData, setLevelUpData] = useState(null);
  const latestUserIdRef = useRef(user?.id || null);
  const setUserRef = useRef(setUser);
  const feedRefreshInFlightRef = useRef(false);
  const feedLoadMoreInFlightRef = useRef(false);
  const nextPostsOffsetRef = useRef(0);
  const hasMorePostsRef = useRef(true);
  const serverCountedPostIdsRef = useRef(new Set());
  const temporaryPinSignatureRef = useRef(null);
  const feedModeRef = useRef("chronological");
  const feedSnapshotTokenRef = useRef("");
  const pendingActiveSessionRefreshRef = useRef(false);
  const pendingActiveSessionRefreshTimerRef = useRef(null);
  const pickedCoordinatesRef = useRef("");
  const pendingEditMapPickRef = useRef(null);
  const pickerSourceRef = useRef(null);
  const pickerReturnInProgressRef = useRef(false);
  const recentLocalPostUpdatesRef = useRef(new Map());
  const recentPostLikeUpdatesRef = useRef(new Map());
  const createPostOpenTrackedRef = useRef(false);
  const initialFeedStartedRef = useRef(false);
  const authoritativeRestoreRequestRef = useRef(null);
  const authTransitioningRef = useRef(Boolean(authTransitioning));
  const levelProgressRef = useRef({
    userId: null,
    level: null,
    rankName: "",
    badgeKey: "",
    hydrated: false,
  });

  useEffect(() => {
    latestUserIdRef.current = user?.id || null;
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) return;

    setPosts([]);
    setInitialFeedResolved(false);
    nextPostsOffsetRef.current = 0;
    serverCountedPostIdsRef.current.clear();
    temporaryPinSignatureRef.current = null;
    feedModeRef.current = "chronological";
    feedSnapshotTokenRef.current = "";
    hasMorePostsRef.current = false;
    setHasMorePosts(false);
    setIsLoadingMorePosts(false);
    recentLocalPostUpdatesRef.current.clear();
    recentPostLikeUpdatesRef.current.clear();
    pendingActiveSessionRefreshRef.current = false;
    authoritativeRestoreRequestRef.current = null;
    setAuthoritativeRestoreState({ requestKey: "", result: null });
  }, [user?.id]);

  useEffect(() => {
    authTransitioningRef.current = Boolean(authTransitioning);
  }, [authTransitioning]);

  useEffect(() => {
    setCatchModalActive(showCreatePost, { reason: "create_post_modal" });
    if (showCreatePost && user?.id && !createPostOpenTrackedRef.current) {
      createPostOpenTrackedRef.current = true;
      trackProductEvent("create_post_opened", {
        featureArea: "create_post",
        metadata: { source: "feed_composer" },
      });
    }
    if (!showCreatePost) {
      createPostOpenTrackedRef.current = false;
    }
    return () => {
      setCatchModalActive(false, { reason: "create_post_modal_unmount" });
    };
  }, [showCreatePost, user?.id]);

  useEffect(() => {
    setCatchSubmitActive(isCreatePostSubmitting, {
      reason: "create_post_submit",
    });
    return () => {
      setCatchSubmitActive(false, { reason: "create_post_submit_unmount" });
    };
  }, [isCreatePostSubmitting]);

  useEffect(() => {
    setUserRef.current = setUser;
  }, [setUser]);

  useEffect(() => {
    const currentUserId = user?.id || null;
    const parsedLevel = Number(user?.progression?.currentLevel);
    const currentLevel = Number.isFinite(parsedLevel)
      ? Math.max(1, Math.floor(parsedLevel))
      : 1;
    const currentRankName = user?.progression?.currentRankName || "";
    const currentBadgeKey = user?.progression?.currentBadgeKey || "";
    const previous = levelProgressRef.current;

    if (!currentUserId) {
      setLevelUpData(null);
      levelProgressRef.current = {
        userId: null,
        level: null,
        rankName: "",
        badgeKey: "",
        hydrated: false,
      };
      return;
    }

    if (!previous.hydrated || previous.userId !== currentUserId) {
      setLevelUpData(null);
      levelProgressRef.current = {
        userId: currentUserId,
        level: currentLevel,
        rankName: currentRankName,
        badgeKey: currentBadgeKey,
        hydrated: true,
      };
      return;
    }

    if (currentLevel > (previous.level || 1)) {
      const hasRankChanged =
        Boolean(currentRankName) && currentRankName !== previous.rankName;
      const hasBadgeChanged =
        Boolean(currentBadgeKey) && currentBadgeKey !== previous.badgeKey;

      setLevelUpData({
        level: currentLevel,
        rankName: currentRankName,
        badgeKey: hasBadgeChanged ? currentBadgeKey : "",
        hasRankChanged,
      });
    }

    levelProgressRef.current = {
      userId: currentUserId,
      level: currentLevel,
      rankName: currentRankName,
      badgeKey: currentBadgeKey,
      hydrated: true,
    };
  }, [
    user?.id,
    user?.progression?.currentLevel,
    user?.progression?.currentRankName,
    user?.progression?.currentBadgeKey,
  ]);

  useEffect(() => {
    const handlePostLiked = (event) => {
      const {
        postId,
        liked,
        likesCount,
        actorUserId,
        mutationPhase = "authoritative",
        mutationVersion = 0,
        mutationUpdatedAt = Date.now(),
      } = event.detail;
      const currentMutation = recentPostLikeUpdatesRef.current.get(postId);

      if (
        currentMutation &&
        Number(currentMutation.mutationVersion || 0) > Number(mutationVersion || 0)
      ) {
        return;
      }

      recentPostLikeUpdatesRef.current.set(postId, {
        liked,
        likesCount,
        actorUserId,
        mutationPhase,
        mutationVersion,
        mutationUpdatedAt,
      });

      setPosts((prev) =>
        prev.map((post) => {
          if (post.id !== postId) return post;

          return applyPostLikeMutation(post, {
            liked,
            likesCount,
            actorUserId,
          });
        })
      );
    };

    window.addEventListener("postLiked", handlePostLiked);
    return () => window.removeEventListener("postLiked", handlePostLiked);
  }, []);

  const selectedPost = useMemo(
    () => posts.find((post) => post?.id === selectedPostId) || null,
    [posts, selectedPostId]
  );

  const setSelectedPost = useCallback((postOrPostId) => {
    if (!postOrPostId) {
      setSelectedPostId(null);
      return;
    }

    if (typeof postOrPostId === "string") {
      setSelectedPostId(postOrPostId);
      return;
    }

    setSelectedPostId(postOrPostId.id || null);
  }, []);

  const setShowInfo = useCallback((nextValue) => {
    const resolvedValue =
      typeof nextValue === "function" ? nextValue(Boolean(showInfo)) : nextValue;
    const nextShowInfo = Boolean(resolvedValue);

    if (!nextShowInfo) {
      setSelectedPostId(null);
    }

    _setShowInfo(nextShowInfo);
  }, [showInfo]);

  useEffect(() => {
    if (!showInfo || !selectedPostId) {
      return;
    }

    if (!selectedPost) {
      _setShowInfo(false);
      setSelectedPostId(null);
    }
  }, [showInfo, selectedPost, selectedPostId]);

  useEffect(() => {
    const handlePostCommented = (event) => {
      const { postId, comment } = event.detail;

      setPosts((prev) =>
        prev.map((post) => {
          if (post.id !== postId) return post;
          const comments = Array.isArray(post.comments) ? post.comments : [];
          return {
            ...post,
            comments: [...comments, comment],
          };
        })
      );
    };

    window.addEventListener("postCommented", handlePostCommented);
    return () =>
      window.removeEventListener("postCommented", handlePostCommented);
  }, []);

  useEffect(() => {
    const handleMapPicked = (event) => {
      const { lat, lng } = event.detail || {};
      if (lat == null || lng == null) return;

      const formatted = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      pickedCoordinatesRef.current = formatted;
      setPickedCoordinates(formatted);
    };

    window.addEventListener("mapLocationPicked", handleMapPicked);
    return () =>
      window.removeEventListener("mapLocationPicked", handleMapPicked);
  }, []);

  useEffect(() => {
    const handleCommentLiked = (event) => {
      const { postId, commentId, likes } = event.detail;

      setPosts((prev) =>
        prev.map((post) => {
          if (post.id !== postId) return post;
          return {
            ...post,
            comments: (post.comments || []).map((comment) =>
              comment.id === commentId ? { ...comment, likes } : comment
            ),
          };
        })
      );
    };

    window.addEventListener("commentLiked", handleCommentLiked);
    return () =>
      window.removeEventListener("commentLiked", handleCommentLiked);
  }, []);

  useEffect(() => {
    const handlePostShared = (event) => {
      const { post } = event.detail || {};
      if (!post?.id) return;

      setPosts((prev) =>
        mergeFeedPostsByMode(feedModeRef.current, prev, [post])
      );
    };

    window.addEventListener("postShared", handlePostShared);
    return () =>
      window.removeEventListener("postShared", handlePostShared);
  }, []);

  useEffect(() => {
    const handleCommentReplied = (event) => {
      const { postId, commentId, reply } = event.detail;

      setPosts((prev) =>
        prev.map((post) => {
          if (post.id !== postId) return post;
          return {
            ...post,
            comments: (post.comments || []).map((comment) =>
              comment.id === commentId
                ? { ...comment, replies: [...(comment.replies || []), reply] }
                : comment
            ),
          };
        })
      );
    };

    window.addEventListener("commentReplied", handleCommentReplied);
    return () =>
      window.removeEventListener("commentReplied", handleCommentReplied);
  }, []);

  useEffect(() => {
    const handleReplyLiked = (event) => {
      const { postId, commentId, replyId, likes } = event.detail;

      setPosts((prev) =>
        prev.map((post) => {
          if (post.id !== postId) return post;
          return {
            ...post,
            comments: (post.comments || []).map((comment) => {
              if (comment.id !== commentId) return comment;
              return {
                ...comment,
                replies: (comment.replies || []).map((reply) =>
                  reply.id === replyId ? { ...reply, likes } : reply
                ),
              };
            }),
          };
        })
      );
    };

    window.addEventListener("replyLiked", handleReplyLiked);
    return () =>
      window.removeEventListener("replyLiked", handleReplyLiked);
  }, []);

  const loadAll = useCallback(async ({
    requireActiveSession = true,
    pollMode = false,
    authoritativeReset = false,
  } = {}) => {
    if (pollMode && feedModeRef.current === PERSONALIZED_FEED_MODE) {
      return;
    }
    if (shouldBlockBackgroundRefresh()) {
      reportBackgroundRefreshBlocked({ source: "posts_feed" });
      return;
    }

    if (feedRefreshInFlightRef.current) {
      if (requireActiveSession) {
        pendingActiveSessionRefreshRef.current = true;
      }
      return;
    }

    if (authTransitioningRef.current) {
      return;
    }

    const authToken = getActiveAwareAuthToken(window);
    const requestUserId = latestUserIdRef.current;
    const requestSessionKey = getFeedSessionKey(requestUserId, authToken);
    const hasActiveSession = Boolean(requestSessionKey);

    if (requireActiveSession && !hasActiveSession) {
      return;
    }

    if (!pollMode) {
      initialFeedStartedRef.current = true;
    }

    feedRefreshInFlightRef.current = true;
    const requestStartedAt = Date.now();

    try {
      const feedQuery = new URLSearchParams({
        limit: String(FEED_PAGE_LIMIT),
        offset: "0",
      });

      const feedRes = await fetch(
        `${API_BASE}/posts?${feedQuery.toString()}`,
        withApiCredentials(
          authToken
            ? {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              }
            : undefined
        )
      );
      if (!feedRes.ok) {
        throw new Error(`Feed request failed (${feedRes.status})`);
      }
      const responseMetadata = getFeedResponseMetadata(feedRes);
      const feedPosts = await feedRes.json();
      const normalizedFeedPosts = normalizeFeedPosts(feedPosts);
      const likeProtectedFeedPosts = applyRecentPostLikeUpdates(
        normalizedFeedPosts,
        recentPostLikeUpdatesRef.current,
        requestStartedAt
      );
      const merged = applyRecentLocalPostUpdates(
        likeProtectedFeedPosts,
        recentLocalPostUpdatesRef.current
      );
      const incomingPinSignature = getTemporaryPinSignature(feedPosts);

      const latestAuthToken = getActiveAwareAuthToken(window);
      const latestUserId = latestUserIdRef.current;
      const latestSessionKey = getFeedSessionKey(latestUserId, latestAuthToken);
      const sessionStillActive =
        !authTransitioningRef.current &&
        Boolean(requestSessionKey) &&
        latestSessionKey === requestSessionKey;
      const canApplyFeed = requestSessionKey
        ? latestSessionKey === requestSessionKey
        : !latestUserId && !latestAuthToken;

      if (!canApplyFeed || authTransitioningRef.current) {
        return;
      }

      cleanupAcceptedPostLikeUpdates(
        recentPostLikeUpdatesRef.current,
        requestStartedAt
      );
      if (responseMetadata.mode === PERSONALIZED_FEED_MODE) {
        const publicRowCount = countPublicFeedRows(feedPosts);
        feedModeRef.current = PERSONALIZED_FEED_MODE;
        feedSnapshotTokenRef.current = responseMetadata.snapshotToken;
        serverCountedPostIdsRef.current = new Set(
          getPublicFeedPostIds(feedPosts)
        );
        temporaryPinSignatureRef.current = incomingPinSignature;
        nextPostsOffsetRef.current = publicRowCount;
        hasMorePostsRef.current = publicRowCount === FEED_PAGE_LIMIT;
        setHasMorePosts(hasMorePostsRef.current);
        setPosts(merged);
      } else if (authoritativeReset) {
        feedModeRef.current = "chronological";
        feedSnapshotTokenRef.current = "";
        const publicRowCount = countPublicFeedRows(feedPosts);
        serverCountedPostIdsRef.current = new Set(
          getPublicFeedPostIds(feedPosts)
        );
        temporaryPinSignatureRef.current = incomingPinSignature;
        nextPostsOffsetRef.current = publicRowCount;
        hasMorePostsRef.current = publicRowCount === FEED_PAGE_LIMIT;
        setHasMorePosts(hasMorePostsRef.current);
        setPosts(sortGlobalFeedPosts(merged));
      } else if (
        temporaryPinSignatureRef.current !== null &&
        temporaryPinSignatureRef.current !== incomingPinSignature
      ) {
        feedModeRef.current = "chronological";
        feedSnapshotTokenRef.current = "";
        const publicRowCount = countPublicFeedRows(feedPosts);
        serverCountedPostIdsRef.current = new Set(
          getPublicFeedPostIds(feedPosts)
        );
        temporaryPinSignatureRef.current = incomingPinSignature;
        nextPostsOffsetRef.current = publicRowCount;
        hasMorePostsRef.current = publicRowCount === FEED_PAGE_LIMIT;
        setHasMorePosts(hasMorePostsRef.current);
        setPosts(sortGlobalFeedPosts(merged));
      } else {
        feedModeRef.current = "chronological";
        feedSnapshotTokenRef.current = "";
        temporaryPinSignatureRef.current = incomingPinSignature;
        const newServerPostIds = getPublicFeedPostIds(feedPosts).filter(
          (postId) => !serverCountedPostIdsRef.current.has(postId)
        );
        newServerPostIds.forEach((postId) =>
          serverCountedPostIdsRef.current.add(postId)
        );
        nextPostsOffsetRef.current += newServerPostIds.length;
        setPosts((current) =>
          mergeGlobalFeedPostsById(
            clearTemporaryPinMetadata(current),
            merged
          )
        );
      }

      if (authToken && sessionStillActive && !pollMode) {
        const meRes = await fetch(`${API_BASE}/auth/me`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (meRes.ok) {
          const me = await meRes.json();
          const currentAuthToken = getActiveAwareAuthToken(window);
          const currentUserId = latestUserIdRef.current;

          if (currentAuthToken === authToken && currentUserId) {
            setUserRef.current(me);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load posts/trips:", err);
    } finally {
      const pendingActiveSessionRefresh =
        pendingActiveSessionRefreshRef.current &&
        Boolean(
          getFeedSessionKey(
            latestUserIdRef.current,
            getActiveAwareAuthToken(window)
          )
        ) &&
        !authTransitioningRef.current;

      if (
        !pollMode &&
        initialFeedStartedRef.current &&
        !pendingActiveSessionRefresh
      ) {
        setInitialFeedResolved(true);
      }
      feedRefreshInFlightRef.current = false;

      if (pendingActiveSessionRefresh) {
        pendingActiveSessionRefreshRef.current = false;

        if (pendingActiveSessionRefreshTimerRef.current != null) {
          window.clearTimeout(pendingActiveSessionRefreshTimerRef.current);
        }

        pendingActiveSessionRefreshTimerRef.current = window.setTimeout(() => {
          pendingActiveSessionRefreshTimerRef.current = null;
          loadAll({ requireActiveSession: true });
        }, 0);
      } else if (
        !getFeedSessionKey(
          latestUserIdRef.current,
          getActiveAwareAuthToken(window)
        ) ||
        authTransitioningRef.current
      ) {
        pendingActiveSessionRefreshRef.current = false;
      }
    }
  }, []);

  const loadMorePosts = useCallback(async () => {
    if (
      feedLoadMoreInFlightRef.current ||
      feedRefreshInFlightRef.current ||
      !hasMorePostsRef.current ||
      authTransitioningRef.current
    ) {
      return false;
    }

    const authToken = getActiveAwareAuthToken(window);
    const requestUserId = latestUserIdRef.current;
    const requestSessionKey = getFeedSessionKey(requestUserId, authToken);
    if (!requestSessionKey) return false;

    const requestOffset = nextPostsOffsetRef.current;
    const requestStartedAt = Date.now();
    feedLoadMoreInFlightRef.current = true;
    setIsLoadingMorePosts(true);

    try {
      const query = new URLSearchParams({
        limit: String(FEED_PAGE_LIMIT),
        offset: String(requestOffset),
      });
      const requestedFeedMode = feedModeRef.current;
      const requestedSnapshotToken = feedSnapshotTokenRef.current;
      if (
        requestedFeedMode === PERSONALIZED_FEED_MODE &&
        requestedSnapshotToken
      ) {
        query.set("feed_snapshot", requestedSnapshotToken);
      }
      const response = await fetch(
        `${API_BASE}/posts?${query.toString()}`,
        withApiCredentials(
          authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined
        )
      );
      if (!response.ok) throw new Error(`Feed page request failed (${response.status})`);

      const responseMetadata = getFeedResponseMetadata(response);
      if (
        requestedFeedMode === PERSONALIZED_FEED_MODE &&
        (
          responseMetadata.mode !== PERSONALIZED_FEED_MODE ||
          responseMetadata.snapshotToken !== requestedSnapshotToken
        )
      ) {
        throw new Error("Ranked feed snapshot changed during pagination");
      }
      const feedPosts = await response.json();
      const normalizedPosts = applyRecentLocalPostUpdates(
        applyRecentPostLikeUpdates(
          normalizeFeedPosts(feedPosts),
          recentPostLikeUpdatesRef.current,
          requestStartedAt
        ),
        recentLocalPostUpdatesRef.current
      );
      const latestSessionKey = getFeedSessionKey(
        latestUserIdRef.current,
        getActiveAwareAuthToken(window)
      );
      if (latestSessionKey !== requestSessionKey || authTransitioningRef.current) {
        return false;
      }

      const publicRowCount = countPublicFeedRows(feedPosts);
      getPublicFeedPostIds(feedPosts).forEach((postId) =>
        serverCountedPostIdsRef.current.add(postId)
      );
      nextPostsOffsetRef.current = requestOffset + publicRowCount;
      hasMorePostsRef.current = publicRowCount === FEED_PAGE_LIMIT;
      setHasMorePosts(hasMorePostsRef.current);
      setPosts((current) =>
        mergeFeedPostsByMode(
          requestedFeedMode,
          current,
          normalizedPosts,
          { incomingWins: false }
        )
      );
      return true;
    } catch (error) {
      console.error("Failed to load more posts:", error);
      hasMorePostsRef.current = false;
      setHasMorePosts(false);
      return false;
    } finally {
      feedLoadMoreInFlightRef.current = false;
      setIsLoadingMorePosts(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    setInitialFeedResolved(false);
    setPosts([]);
    nextPostsOffsetRef.current = 0;
    serverCountedPostIdsRef.current.clear();
    temporaryPinSignatureRef.current = null;
    feedModeRef.current = "chronological";
    feedSnapshotTokenRef.current = "";
    hasMorePostsRef.current = true;
    setHasMorePosts(true);
    loadAll({ requireActiveSession: true, authoritativeReset: true });
  }, [loadAll, user?.id]);

  useEffect(() => {
    const restoreKey = String(authoritativeRestoreKey || "").trim();
    const restorePostId = String(authoritativeRestorePostId || "").trim();
    if (!user?.id || !restoreKey || !restorePostId || authTransitioning) {
      return undefined;
    }

    const authToken = getActiveAwareAuthToken(window);
    const sessionKey = getFeedSessionKey(user.id, authToken);
    if (!sessionKey) return undefined;

    const requestKey = `${sessionKey}:${restoreKey}:${restorePostId}`;
    let request = authoritativeRestoreRequestRef.current;
    if (request?.requestKey !== requestKey) {
      request = {
        requestKey,
        promise: loadAuthoritativePostRestore(restorePostId, { authToken }),
      };
      authoritativeRestoreRequestRef.current = request;
      setAuthoritativeRestoreState({ requestKey, result: null });
    }

    let cancelled = false;
    request.promise.then((result) => {
      if (cancelled) return;

      const latestSessionKey = getFeedSessionKey(
        latestUserIdRef.current,
        getActiveAwareAuthToken(window)
      );
      if (
        authoritativeRestoreRequestRef.current?.requestKey !== requestKey ||
        latestSessionKey !== sessionKey ||
        authTransitioningRef.current
      ) {
        return;
      }

      if (result.status === "found") {
        setPosts((current) =>
          mergeFeedPostsByMode(
            feedModeRef.current,
            current,
            [result.post]
          )
        );
      } else if (result.status === "missing") {
        setPosts((current) =>
          current.filter(
            (post) => String(post?.id || "").trim() !== restorePostId
          )
        );
      }
      setAuthoritativeRestoreState({ requestKey, result });
    });

    return () => {
      cancelled = true;
    };
  }, [
    authoritativeRestoreKey,
    authoritativeRestorePostId,
    authTransitioning,
    user?.id,
  ]);

  useEffect(() => {
    const handlePostsUpdated = () => {
      loadAll({ requireActiveSession: true });
    };

    window.addEventListener("postsUpdated", handlePostsUpdated);

    return () => {
      window.removeEventListener("postsUpdated", handlePostsUpdated);
    };
  }, [loadAll]);

  useEffect(() => {
    return () => {
      if (pendingActiveSessionRefreshTimerRef.current != null) {
        window.clearTimeout(pendingActiveSessionRefreshTimerRef.current);
        pendingActiveSessionRefreshTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const refreshEvent = getCatchSubmitRefreshEventName();
    const handleCatchSubmitRefresh = (event) => {
      const attemptId = event?.detail?.attemptId ?? null;
      logCatchTx("refresh_started", { attemptId });
      const refreshPromise = loadAll({ requireActiveSession: true });
      Promise.resolve(refreshPromise).finally(() => {
        logCatchTx("refresh_finished", { attemptId });
      });
    };

    window.addEventListener(refreshEvent, handleCatchSubmitRefresh);
    return () => {
      window.removeEventListener(refreshEvent, handleCatchSubmitRefresh);
    };
  }, [loadAll]);

  useEffect(() => {
    if (!user?.id || !isFeedRoute) {
      return undefined;
    }

    const refreshPosts = () => {
      if (shouldBlockBackgroundRefresh()) {
        reportBackgroundRefreshBlocked({ source: "posts_feed_interval" });
        return;
      }
      if (typeof document !== "undefined" && document.hidden) {
        return;
      }

      loadAll({ requireActiveSession: true, pollMode: true });
    };

    const postsRefreshIntervalId = window.setInterval(
      refreshPosts,
      FEED_POLL_INTERVAL_MS
    );

    return () => {
      window.clearInterval(postsRefreshIntervalId);
    };
  }, [isFeedRoute, loadAll, user?.id]);

  useEffect(() => {
    const handler = () => {
      setActiveTab("map");
    };

    window.addEventListener("navigateToMap", handler);
    return () => {
      window.removeEventListener("navigateToMap", handler);
    };
  }, [setActiveTab]);

  useEffect(() => {
    const handler = () => setActiveTab("map");
    window.addEventListener("switchToMapTab", handler);
    return () => window.removeEventListener("switchToMapTab", handler);
  }, [setActiveTab]);

  useEffect(() => {
    const handleGoToMapPicker = (event) => {
      const source = event.detail?.source || "createPost";
      setPickedCoordinates("");
      pickedCoordinatesRef.current = "";
      pickerSourceRef.current = source;
      pickerReturnInProgressRef.current = false;
      setPickerSource(source);
      onSuppressPickerOpenNavigation?.();
      if (source === "editPost") {
        pendingEditMapPickRef.current = {
          postId: event.detail?.postId || "",
          draftFormState: event.detail?.draftFormState || null,
        };
        setShowProfile(false);
      } else if (source === "pastTripCatch") {
        setShowCreatePost(false);
      } else {
        setDraftCatchFormState(event.detail?.draftFormState || null);
        setShowCreatePost(false);
      }
      setIsPickingLocation(true);
      setActiveTab("map");
    };

    const handleReturnToPost = () => {
      if (pickerReturnInProgressRef.current) {
        return;
      }
      pickerReturnInProgressRef.current = true;

      const source = pickerSourceRef.current || pickerSource;
      setIsPickingLocation(false);
      if (source === "createPost") {
        if (typeof onForcePickerReturnNavigation === "function") {
          onForcePickerReturnNavigation(source);
        } else {
          setActiveTab("feed");
        }
        onSuppressPickerReturnNavigation?.();
        setShowCreatePost(true);
        pickerSourceRef.current = null;
        setPickerSource(null);
        return;
      }

      if (source === "editPost") {
        const pendingEdit = pendingEditMapPickRef.current;
        if (typeof onForcePickerReturnNavigation === "function") {
          onForcePickerReturnNavigation(source);
        } else {
          setActiveTab("feed");
        }
        onSuppressPickerReturnNavigation?.();
        setShowProfile(true);
        setEditPostMapSelection({
          postId: pendingEdit?.postId || "",
          coordinates: pickedCoordinatesRef.current,
          draftFormState: pendingEdit?.draftFormState || null,
          requestedAt: Date.now(),
        });
        pendingEditMapPickRef.current = null;
        pickerSourceRef.current = null;
        pickedCoordinatesRef.current = "";
        setPickedCoordinates("");
        setPickerSource(null);
        return;
      }

      if (source === "pastTripCatch") {
        if (typeof window !== "undefined") {
          try {
            window.sessionStorage.setItem(
              PAST_TRIP_CATCH_MAP_RETURN_KEY,
              JSON.stringify({
                coordinates: pickedCoordinatesRef.current,
                selectionId: Date.now(),
                requestedAt: Date.now(),
              })
            );
          } catch {
            // Ignore storage failures; the picker can still close cleanly.
          }
        }

        if (typeof onForcePickerReturnNavigation === "function") {
          onForcePickerReturnNavigation(source);
        } else {
          setActiveTab("feed");
        }
        onSuppressPickerReturnNavigation?.();
        pickerSourceRef.current = null;
        pickedCoordinatesRef.current = "";
        setPickedCoordinates("");
        setPickerSource(null);
        return;
      }

      pickerReturnInProgressRef.current = false;
    };

    window.addEventListener("goToMapPicker", handleGoToMapPicker);
    window.addEventListener("returnToPost", handleReturnToPost);

    return () => {
      window.removeEventListener("goToMapPicker", handleGoToMapPicker);
      window.removeEventListener("returnToPost", handleReturnToPost);
    };
  }, [
    onSuppressPickerOpenNavigation,
    onSuppressPickerReturnNavigation,
    onForcePickerReturnNavigation,
    pickerSource,
    setActiveTab,
    setShowProfile,
  ]);

  const saveMapSpotFromPost = async (post) => {
    const coords = post?.catch?.coordinates;
    if (!coords || coords === "-") return;

    window.dispatchEvent(new Event("spotsUpdated"));
  };

  const clearEditPostMapSelection = useCallback(() => {
    setEditPostMapSelection(null);
  }, []);

  const handleAddPost = (post, options = {}) => {
    const { skipRefreshEvents = false } = options;
    setPosts((prev) =>
      mergeFeedPostsByMode(feedModeRef.current, prev, [post])
    );
    trackProductEvent("create_post_submitted", {
      featureArea: "create_post",
      metadata: {
        post_type: post?.postType === "opinion" ? "opinion" : "catch",
      },
    });
    if (!skipRefreshEvents) {
      window.dispatchEvent(new Event("postsUpdated"));
      saveMapSpotFromPost(post);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!postId || !user?.id) return false;
    const deletedPost = posts.find((post) => post.id === postId);

    try {
      const deleteResult = await deletePostRequest(postId);
      setPosts((prev) => prev.filter((post) => post.id !== postId));
      if (serverCountedPostIdsRef.current.delete(String(postId))) {
        nextPostsOffsetRef.current = Math.max(0, nextPostsOffsetRef.current - 1);
      }

      if (selectedPost?.id === postId) {
        setShowInfo(false);
        setSelectedPost(null);
      }

      if (sharePost?.id === postId) {
        setSharePost(null);
      }

      if (deletedPost?.catch?.coordinates) {
        window.dispatchEvent(new Event("spotsUpdated"));
      }

      return {
        ok: true,
        alreadyDeleted: Boolean(deleteResult?.alreadyDeleted),
      };
    } catch (err) {
      console.error("Failed to delete post:", err);
      return {
        ok: false,
        status: err?.status || null,
        category: err?.category || "network",
        message: getPostDeleteErrorMessage(err),
      };
    }
  };

  const handleUpdatePost = (updatedPost, previousPost) => {
    if (!updatedPost?.id) return false;

    const existingPost = posts.find((post) => post.id === updatedPost.id);
    const mergedExistingPost = existingPost
      ? mergeUpdatedPost(existingPost, updatedPost)
      : updatedPost;
    recentLocalPostUpdatesRef.current.set(updatedPost.id, {
      post: mergedExistingPost,
      expiresAt: Date.now() + RECENT_LOCAL_POST_UPDATE_TTL_MS,
    });
    const shouldRefreshSpots =
      didPostInfoContextChange(previousPost || existingPost, mergedExistingPost) ||
      didPostInfoContextChange(existingPost, mergedExistingPost);

    setPosts((prev) =>
      prev.map((post) => {
        if (post.id !== updatedPost.id) {
          return post;
        }

        return mergeUpdatedPost(post, updatedPost);
      })
    );

    if (selectedPost?.id === updatedPost.id) {
      const nextSelectedPost = mergeUpdatedPost(selectedPost, updatedPost);
      setSelectedPost(nextSelectedPost);
    }

    if (sharePost?.id === updatedPost.id) {
      setSharePost((current) =>
        current?.id === updatedPost.id ? mergeUpdatedPost(current, updatedPost) : current
      );
    }

    if (shouldRefreshSpots) {
      window.dispatchEvent(new Event("spotsUpdated"));
    }

    return true;
  };

  const openPostInProfileContext = useCallback((
    postId,
    fallbackUsername = "",
    fallbackUserId = "",
    options = {}
  ) => {
    const normalizedPostId = (postId || "").trim();
    if (!normalizedPostId) return;

    const matchedPost = posts.find((post) => post?.id === normalizedPostId);
    const targetUsername = (matchedPost?.user || fallbackUsername || "").trim();
    const targetUserId = (matchedPost?.userId || fallbackUserId || "").trim();
    const isOwnPost =
      (user?.id && targetUserId && user.id === targetUserId) ||
      (user?.username && targetUsername && user.username === targetUsername);
    const focusRequest = {
      postId: normalizedPostId,
      commentId: (options?.commentId || "").trim() || undefined,
      requestedAt: Date.now(),
    };

    setShowInfo(false);
    setSelectedPost(null);

    if (isOwnPost) {
      setSelectedUser(null);
      setShowUserProfile(false);
      setUserProfileFocusPostRequest(null);
      setShowProfile(true);
      setProfileFocusPostRequest(focusRequest);
      return;
    }

    if (!targetUsername) {
      return;
    }

    setShowProfile(false);
    setProfileFocusPostRequest(null);
    setSelectedUser(targetUsername);
    setShowUserProfile(true);
    setUserProfileFocusPostRequest(focusRequest);
  }, [
    posts,
    setProfileFocusPostRequest,
    setSelectedPost,
    setSelectedUser,
    setShowInfo,
    setShowProfile,
    setShowUserProfile,
    setUserProfileFocusPostRequest,
    user?.id,
    user?.username,
  ]);

  const handleOpenSharedPostFromChat = (sharedPost) => {
    openPostInProfileContext(sharedPost?.id, sharedPost?.user, sharedPost?.userId);
  };

  const handleOpenPostFromNotification = (notification) => {
    openPostInProfileContext(notification?.postId, "", "", {
      commentId: notification?.commentId,
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const postId = (params.get("post") || "").trim();
    if (!postId) return;
    const matchedPost = posts.find((post) => post?.id === postId);
    if (!matchedPost) return;

    openPostInProfileContext(postId, matchedPost.user, matchedPost.userId, {
      commentId: (params.get("comment") || "").trim(),
      replyId: (params.get("reply") || "").trim(),
    });
    params.delete("notification");
    params.delete("post");
    params.delete("comment");
    params.delete("reply");
    const nextQuery = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`
    );
  }, [openPostInProfileContext, posts]);

  const handleCreatePostClose = (hideOnly, options = {}) => {
    const resolvedHideOnly =
      typeof hideOnly === "object" ? hideOnly.hideOnly : hideOnly;
    const force =
      typeof hideOnly === "object" ? Boolean(hideOnly.force) : options.force;

    if (isCreatePostSubmitting && !force) {
      return;
    }

    if (resolvedHideOnly === true) {
      setShowCreatePost(false);
    } else if (resolvedHideOnly === false) {
      setShowCreatePost(true);
    } else {
      setPickedCoordinates("");
      setDraftCatchMedia(null);
      setDraftCatchFormState(null);
      setDraftExifDate(null);
      setDraftExifTime(null);
      setShowCreatePost(false);
    }
  };

  const handleCreatePostOverlayClick = (event) => {
    if (isCreatePostSubmitting) {
      return;
    }

    if (event.target === event.currentTarget) {
      handleCreatePostClose();
    }
  };

  const normalizedAuthoritativeRestoreKey = String(
    authoritativeRestoreKey || ""
  ).trim();
  const normalizedAuthoritativeRestorePostId = String(
    authoritativeRestorePostId || ""
  ).trim();
  const currentAuthToken = getActiveAwareAuthToken(window);
  const currentRestoreSessionKey = user?.id
    ? getFeedSessionKey(user.id, currentAuthToken)
    : null;
  const currentRestoreRequestKey =
    !authTransitioning &&
    currentRestoreSessionKey &&
    normalizedAuthoritativeRestoreKey &&
    normalizedAuthoritativeRestorePostId
      ? `${currentRestoreSessionKey}:${normalizedAuthoritativeRestoreKey}:${normalizedAuthoritativeRestorePostId}`
      : "";
  const hasAuthoritativeRestoreRequest = Boolean(
    normalizedAuthoritativeRestoreKey && normalizedAuthoritativeRestorePostId
  );
  const authoritativePostRestoreResult =
    hasAuthoritativeRestoreRequest && !authTransitioning && !user?.id
      ? { status: "failed" }
      : currentRestoreRequestKey &&
          authoritativeRestoreState.requestKey === currentRestoreRequestKey
        ? authoritativeRestoreState.result
        : null;

  return {
    posts,
    filteredPosts: posts,
    startup: {
      initialFeedResolved,
      authoritativePostRestoreResult,
    },
    pagination: {
      hasMorePosts,
      isLoadingMorePosts,
      loadMorePosts,
    },
    info: {
      showInfo,
      setShowInfo,
      selectedPost,
      setSelectedPost,
    },
    composer: {
      showCreatePost,
      setShowCreatePost,
      isCreatePostSubmitting,
      setIsCreatePostSubmitting,
      pickedCoordinates,
      draftCatchMedia,
      setDraftCatchMedia,
      draftCatchFormState,
      setDraftCatchFormState,
      draftExifDate,
      setDraftExifDate,
      draftExifTime,
      setDraftExifTime,
      editPostMapSelection,
      clearEditPostMapSelection,
      isPickingLocation,
    },
    sharing: {
      sharePost,
      setSharePost,
    },
    levelUp: {
      levelUpData,
      setLevelUpData,
    },
    actions: {
      handleAddPost,
      handleDeletePost,
      handleUpdatePost,
      handleOpenSharedPostFromChat,
      handleOpenPostFromNotification,
      handleCreatePostClose,
      handleCreatePostOverlayClick,
    },
  };
}
